import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Store, ArrowRight, Trash2, Clock, Search, Plus, Loader2 } from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';
import FoodImage from '../../components/FoodImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────────────────────────
 * AccountFavorites — Favorite restaurants dashboard.
 *
 * iter90 (Feb 2026): added an inline "Find a restaurant" search
 * input directly on this page so customers can discover and add
 * restaurants to their favorites WITHOUT leaving the account area
 * (previously the only way to favorite something was to go to
 * /restaurants, browse, click into the menu page, tap the heart,
 * then come back here — 4 navigation steps). The search box uses
 * the existing PUBLIC `GET /api/restaurants/search?q=...` endpoint
 * (debounced 250ms), renders a dropdown of matching results, and
 * each row has a one-tap heart that POSTs to the favorites
 * toggle endpoint and immediately surfaces the restaurant in the
 * favorites grid below.
 * ─────────────────────────────────────────────────────────────── */

const AccountFavorites = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [favs, setFavs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  // ─── Inline restaurant search (iter90) ────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const searchBoxRef = useRef(null);

  const load = async () => {
    try {
      const [f, r] = await Promise.all([
        axios.get(`${API}/customer/favorites`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customer/recent-visits`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setFavs(f.data?.favorites || []);
      setRecent(r.data?.visits || []);
    } catch {
      toast.error('Could not load favorites');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (token) load(); }, [token]);

  // Debounced search — fires 250ms after the user stops typing.
  useEffect(() => {
    const term = searchTerm.trim();
    const handle = setTimeout(async () => {
      if (term.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const res = await axios.get(`${API}/restaurants/search?q=${encodeURIComponent(term)}`);
        const list = (res.data?.restaurants || []).slice(0, 8);
        setSearchResults(list);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Close the dropdown when the user clicks outside the search box
  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const favoriteIds = new Set(favs.map((f) => f.restaurant_id));

  const addToFavorites = async (restaurant) => {
    const rid = restaurant.id;
    if (!rid || favoriteIds.has(rid)) return;
    setAddingId(rid);
    try {
      await axios.post(
        `${API}/customer/favorites/${rid}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${restaurant.name} added to Favorites`);
      // Refresh the favorites list so the new card appears below
      const r = await axios.get(`${API}/customer/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFavs(r.data?.favorites || []);
      setSearchTerm('');
      setSearchResults([]);
      setSearchOpen(false);
    } catch {
      toast.error('Could not add to favorites');
    } finally {
      setAddingId(null);
    }
  };

  const removeFav = async (restaurantId) => {
    try {
      await axios.post(
        `${API}/customer/favorites/${restaurantId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setFavs((arr) => arr.filter((f) => f.restaurant_id !== restaurantId));
      toast.success('Removed from favorites');
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <CustomerAccountLayout
      currentKey="favorites"
      title="Favorite Restaurants"
      subtitle="The places you love — easy to reorder, easy to manage."
    >
      {/* ─── Inline restaurant search (iter90) ──────────────────── */}
      <div className="mb-7 sm:mb-8" ref={searchBoxRef} data-testid="favorites-search-block">
        <label className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold flex items-center gap-2 mb-2.5">
          <Search className="w-3.5 h-3.5" /> Find a restaurant
        </label>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            placeholder="Search by name, ZIP, city or cuisine…"
            className="w-full pl-11 pr-12 py-3.5 rounded-2xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[14.5px] focus:outline-none focus:border-yellow-500/60 focus:bg-white/[0.06] transition-colors"
            data-testid="favorites-search-input"
            autoComplete="off"
          />
          {searching && (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-300/70 animate-spin" />
          )}

          {/* Results dropdown */}
          {searchOpen && searchTerm.trim().length >= 2 && (
            <div
              className="absolute left-0 right-0 mt-2 max-h-[420px] overflow-y-auto rounded-2xl border border-yellow-500/25 bg-zinc-950/95 backdrop-blur-md shadow-2xl z-30"
              data-testid="favorites-search-dropdown"
            >
              {searching && searchResults.length === 0 ? (
                <div className="px-4 py-5 text-[13px] text-white/55 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                </div>
              ) : searchResults.length === 0 ? (
                <div className="px-4 py-5 text-[13px] text-white/55" data-testid="favorites-search-empty">
                  No restaurants match &ldquo;{searchTerm.trim()}&rdquo;.
                </div>
              ) : (
                <ul className="divide-y divide-white/[0.06]">
                  {searchResults.map((r) => {
                    const alreadyFav = favoriteIds.has(r.id);
                    return (
                      <li
                        key={r.id}
                        data-testid={`favorites-search-result-${r.id}`}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
                      >
                        <div className="w-11 h-11 rounded-lg bg-zinc-900 overflow-hidden flex items-center justify-center shrink-0">
                          {r.logo_url ? (
                            <FoodImage src={r.logo_url} alt={r.name} variant="thumb" className="w-full h-full" rounded="rounded-lg" />
                          ) : (
                            <Store className="w-5 h-5 text-white/25" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/menu/${r.slug || ''}`}
                            className="block text-[13.5px] font-semibold text-white hover:text-yellow-200 truncate"
                          >
                            {r.name}
                          </Link>
                          <div className="text-[11.5px] text-white/50 truncate">
                            {r.cuisine_type || 'Restaurant'}{r.city ? ` · ${r.city}` : ''}
                          </div>
                        </div>
                        {alreadyFav ? (
                          <span
                            data-testid={`favorites-search-already-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11.5px] font-bold"
                          >
                            <Heart className="w-3 h-3 fill-emerald-300" /> Favorited
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addToFavorites(r)}
                            disabled={addingId === r.id}
                            data-testid={`favorites-search-add-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[11.5px] font-extrabold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {addingId === r.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                            Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <p className="mt-2 text-[11.5px] text-white/45 leading-snug">
          Type at least 2 characters. Tap <strong className="text-white/75">+ Add</strong> to save a restaurant to your Favorites.
        </p>
      </div>

      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="favorites-loading">Loading…</div>
      ) : (
        <>
          {/* Favorites grid */}
          {favs.length === 0 ? (
            <div
              data-testid="favorites-empty"
              className="p-8 sm:p-10 rounded-3xl border border-white/10 bg-white/[0.02] text-center"
            >
              <Heart className="w-8 h-8 text-yellow-300/70 mx-auto mb-4" />
              <h2 className="text-[17px] font-bold text-white tracking-tight">No favorites yet</h2>
              <p className="mt-2 text-[13.5px] text-white/55 max-w-md mx-auto">
                Use the search above to find a restaurant, or tap the heart on any restaurant page to save it here.
              </p>
              <Link
                to="/restaurants"
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13px]"
                data-testid="favorites-empty-cta"
              >
                Browse all restaurants <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="favorites-grid">
              {favs.map((f) => {
                const r = f.restaurant || {};
                return (
                  <div
                    key={f.id}
                    data-testid={`favorite-card-${r.id || f.restaurant_id}`}
                    className="group rounded-2xl border border-white/8 bg-white/[0.02] hover:border-yellow-500/30 hover:bg-white/[0.04] transition-all overflow-hidden"
                  >
                    <div className="aspect-[5/3] bg-zinc-900 relative">
                      {r.logo_url ? (
                        <FoodImage
                          src={r.logo_url}
                          alt={r.name}
                          variant="card"
                          className="w-full h-full opacity-90"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Store className="w-10 h-10 text-white/15" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="text-[15px] font-semibold text-white tracking-tight truncate">{r.name}</div>
                      <div className="mt-0.5 text-[12px] text-white/45 truncate">
                        {r.cuisine_type || 'Restaurant'} {r.address ? `· ${r.address}` : ''}
                      </div>
                      <div className="mt-3.5 flex items-center gap-2">
                        <Link
                          to={`/menu/${r.slug || ''}`}
                          data-testid={`favorite-order-${r.id || f.restaurant_id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[12.5px]"
                        >
                          Order now <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => removeFav(r.id || f.restaurant_id)}
                          data-testid={`favorite-remove-${r.id || f.restaurant_id}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-white/10 hover:border-rose-500/40 text-white/55 hover:text-rose-300 transition-all"
                          aria-label="Remove from favorites"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recently visited */}
          {recent.length > 0 && (
            <div className="mt-10" data-testid="recent-visits-block">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-yellow-300" />
                <h2 className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                  Recently visited
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {recent.slice(0, 5).map((v) => {
                  const r = v.restaurant || {};
                  return (
                    <Link
                      key={v.id}
                      to={`/menu/${r.slug || ''}`}
                      data-testid={`recent-visit-${r.id}`}
                      className="group p-3 rounded-xl border border-white/8 bg-white/[0.02] hover:border-yellow-500/30 hover:bg-white/[0.04] transition-all"
                    >
                      <div className="aspect-square rounded-lg bg-zinc-900 mb-2 overflow-hidden">
                        {r.logo_url ? (
                          <FoodImage
                            src={r.logo_url}
                            alt={r.name}
                            variant="thumb"
                            className="w-full h-full"
                            rounded="rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Store className="w-6 h-6 text-white/15" />
                          </div>
                        )}
                      </div>
                      <div className="text-[12.5px] font-semibold text-white tracking-tight truncate">{r.name}</div>
                      <div className="text-[10.5px] text-white/40 truncate">{r.cuisine_type}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountFavorites;
